/**
 * Redux actions for the Batch Tables (Tabular Review) feature.
 *
 * Naming: "BatchTables" to avoid collision with existing TablesAction.tsx
 * which handles the SSE table-generation feature.
 */

import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import axios from 'axios';
import apis from '../../../utils/apis';
import { showFeedbackModal } from '../UserFeedbackActions';
import {
  runSequentialUpload,
  SkippedFile,
  isPartialUpload,
  describeUploadResult,
  UPLOAD_MAX_FILES_PER_BATCH,
} from '../../../services/uploads/uploadBatching';
import { showSidePanel } from '../sidePanelActions';
import logger from '../../../utils/logger';
import { RootState } from '../..';
import { translate } from '../../../i18n/translate';
import {
  CreateTablePayload,
  UpdateTablePayload,
  CreateColumnPayload,
  UpdateColumnPayload,
  SaveAsTemplatePayload,
  CreateTemplatePayload,
  TableRow,
} from '../../../types/batchTables';
import { readErrorMessage, toErrorText } from '../../../utils/errorMessage';
// ─── Action Types ────────────────────────────────────────────────────────────

export const BATCH_TABLES_LOADING = 'BATCH_TABLES_LOADING';
export const BATCH_TABLES_FETCH_LIST = 'BATCH_TABLES_FETCH_LIST';
export const BATCH_TABLES_APPEND_LIST = 'BATCH_TABLES_APPEND_LIST';
export const BATCH_TABLES_SET_DETAIL = 'BATCH_TABLES_SET_DETAIL';
export const BATCH_TABLES_LOADING_DETAIL = 'BATCH_TABLES_LOADING_DETAIL';
export const BATCH_TABLES_UPDATE_IN_LIST = 'BATCH_TABLES_UPDATE_IN_LIST';
export const BATCH_TABLES_REMOVE_FROM_LIST = 'BATCH_TABLES_REMOVE_FROM_LIST';
export const BATCH_TABLES_SET_TEMPLATES = 'BATCH_TABLES_SET_TEMPLATES';
export const BATCH_TABLES_LOADING_TEMPLATES = 'BATCH_TABLES_LOADING_TEMPLATES';
export const BATCH_TABLES_SET_UPLOADING = 'BATCH_TABLES_SET_UPLOADING';
export const BATCH_TABLES_SET_PROCESSING = 'BATCH_TABLES_SET_PROCESSING';
export const BATCH_TABLES_ERROR = 'BATCH_TABLES_ERROR';
export const BATCH_TABLES_UPDATE_CELL = 'BATCH_TABLES_UPDATE_CELL';
export const BATCH_TABLES_UPDATE_ROW_STATUS = 'BATCH_TABLES_UPDATE_ROW_STATUS';
export const BATCH_TABLES_UPDATE_TABLE_STATUS = 'BATCH_TABLES_UPDATE_TABLE_STATUS';
export const BATCH_TABLES_APPEND_ROWS = 'BATCH_TABLES_APPEND_ROWS';
export const BATCH_TABLES_SET_UPLOAD_PROGRESS = 'BATCH_TABLES_SET_UPLOAD_PROGRESS';
export const BATCH_TABLES_RESET_COLUMN_CELLS = 'BATCH_TABLES_RESET_COLUMN_CELLS';

// SSE Creation
export const BATCH_TABLE_CREATE_RESET = 'BATCH_TABLE_CREATE_RESET';
export const BATCH_TABLE_CREATE_CONNECTING = 'BATCH_TABLE_CREATE_CONNECTING';
export const BATCH_TABLE_CREATE_CONNECTED = 'BATCH_TABLE_CREATE_CONNECTED';
export const BATCH_TABLE_CREATE_STEPS = 'BATCH_TABLE_CREATE_STEPS';
export const BATCH_TABLE_CREATE_STEP = 'BATCH_TABLE_CREATE_STEP';
export const BATCH_TABLE_CREATE_COMPLETED = 'BATCH_TABLE_CREATE_COMPLETED';
export const BATCH_TABLE_CREATE_FAILED = 'BATCH_TABLE_CREATE_FAILED';

// Cell Comments
export const BATCH_TABLES_SET_CELL_COMMENTS = 'BATCH_TABLES_SET_CELL_COMMENTS';
export const BATCH_TABLES_LOADING_CELL_COMMENTS = 'BATCH_TABLES_LOADING_CELL_COMMENTS';
export const BATCH_TABLES_ADD_CELL_COMMENT = 'BATCH_TABLES_ADD_CELL_COMMENT';
export const BATCH_TABLES_UPDATE_CELL_COMMENT = 'BATCH_TABLES_UPDATE_CELL_COMMENT';
export const BATCH_TABLES_REMOVE_CELL_COMMENT = 'BATCH_TABLES_REMOVE_CELL_COMMENT';

// Deep Dive
export const BATCH_TABLES_UPDATE_ROW_DEEP_DIVE = 'BATCH_TABLES_UPDATE_ROW_DEEP_DIVE';

// Playbook Sync
export const BATCH_TABLES_SYNC_PLAYBOOK_START = 'BATCH_TABLES_SYNC_PLAYBOOK_START';
export const BATCH_TABLES_SYNC_PLAYBOOK_DONE = 'BATCH_TABLES_SYNC_PLAYBOOK_DONE';

// Row Jurisdiction
export const BATCH_TABLES_UPDATE_ROW_JURISDICTION = 'BATCH_TABLES_UPDATE_ROW_JURISDICTION';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const handleApiError = (
  error: any,
  dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
  customMessage?: string
) => {
  // Skip cancelled requests (includes licensing errors converted to Cancel by interceptor)
  if (axios.isCancel(error)) return;

  const response = error?.response?.data || {};
  const serverText = toErrorText(response.error) || toErrorText(response.message);
  /*
    THE SERVER'S WORDS SURVIVE THE CUSTOM ONE.

    `customMessage` used to REPLACE them, so "Some documents failed to upload"
    was the whole of what a user saw when the server had said exactly what went
    wrong. The custom message is context - which operation failed - and the
    server text is the reason; a reader needs both.
  */
  const message = customMessage
    ? (serverText ? `${customMessage}: ${serverText}` : customMessage)
    : (serverText || 'Something went wrong. Please try again.');

  dispatch(showFeedbackModal({
    showModal: true,
    message,
    modalType: 'error',
    duration: 5000,
  }));

  dispatch({ type: BATCH_TABLES_ERROR, payload: message });
};

// ─── Table CRUD ──────────────────────────────────────────────────────────────

interface FetchTablesParams {
  page?: number;
  page_size?: number;
  matter_id?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  search_term?: string;
}

export const fetchBatchTables = (params: FetchTablesParams = {}) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    const { page = 1, page_size = 20, matter_id, sort_by, sort_order, search_term } = params;

    dispatch({ type: BATCH_TABLES_LOADING, payload: true });

    try {
      const response = await apis.get('/tables', {
        params: {
          page,
          page_size,
          ...(matter_id ? { matter_id } : {}),
          ...(sort_by ? { sort_by } : {}),
          ...(sort_order ? { sort_order } : {}),
          ...(search_term ? { search_term } : {}),
        },
      });

      if (response.status === 200) {
        const data = response.data;
        dispatch({
          type: page === 1 ? BATCH_TABLES_FETCH_LIST : BATCH_TABLES_APPEND_LIST,
          payload: {
            items: data.items || [],
            total: data.total || 0,
            page: data.page || page,
            total_pages: data.total_pages || 1,
          },
        });
        return data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: BATCH_TABLES_LOADING, payload: false });
    }

    return null;
  };
};

export const fetchBatchTableDetail = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    if (!tableUuid) return null;

    dispatch({ type: BATCH_TABLES_LOADING_DETAIL, payload: true });

    try {
      const response = await apis.get(`/tables/${tableUuid}`);

      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_SET_DETAIL, payload: response.data });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: BATCH_TABLES_LOADING_DETAIL, payload: false });
    }

    return null;
  };
};

export const createBatchTable = (payload: CreateTablePayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/tables', payload);

      if (response.status === 201) {
        // dispatch(showFeedbackModal({
        //   showModal: true,
        //   message: 'Table created successfully',
        //   modalType: 'success',
        //   duration: 3000,
        // }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to create table');
    }

    return null;
  };
};

export const updateBatchTable = (tableUuid: string, payload: UpdateTablePayload) => {
  return async (
    dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
    getState: () => RootState,
  ) => {
    try {
      const response = await apis.put(`/tables/${tableUuid}`, payload);

      if (response.status === 200) {
        // BE returns a trimmed payload ({name, updated, uuid}). Merge it onto whatever
        // we already have in currentTable so rows/columns/metadata aren't wiped - a full
        // replace would set those to undefined and crash the detail page on .length.
        // Fold our outgoing `payload` in too, since the BE accepted it (200) but doesn't
        // echo every field back.
        const current = getState().batchTables.currentTable;
        const merged = current && current.uuid === tableUuid
          ? { ...current, ...payload, ...response.data }
          : { ...payload, ...response.data };
        dispatch({ type: BATCH_TABLES_UPDATE_IN_LIST, payload: merged });
        dispatch({ type: BATCH_TABLES_SET_DETAIL, payload: merged });
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Table updated'),
          modalType: 'success',
          duration: 2000,
        }));
        return merged;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update table');
    }

    return null;
  };
};

export const deleteBatchTable = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/${tableUuid}`);

      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_REMOVE_FROM_LIST, payload: tableUuid });
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Table deleted'),
          modalType: 'success',
          duration: 3000,
        }));
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete table');
    }

    return false;
  };
};

export const deleteBatchTablesBulk = (tableUuids: string[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis({
        method: 'POST',
        url: '/tables/delete_bulk',
        data: { table_uuids: tableUuids },
      });

      if (response.status === 200) {
        const { deleted_count, total_requested, failed } = response.data;
        const allSucceeded = deleted_count === total_requested;

        // Remove successfully deleted tables from the list
        const failedUuids = new Set((failed || []).map((f: any) => f.uuid));
        for (const uuid of tableUuids) {
          if (!failedUuids.has(uuid)) {
            dispatch({ type: BATCH_TABLES_REMOVE_FROM_LIST, payload: uuid });
          }
        }

        dispatch(showFeedbackModal({
          showModal: true,
          message: allSucceeded
            ? `${deleted_count} table${deleted_count > 1 ? 's' : ''} deleted successfully`
            : `${deleted_count} of ${total_requested} tables deleted${failed?.length ? `. ${failed.length} failed.` : ''}`,
          modalType: allSucceeded ? 'success' : 'warning',
          duration: 3000,
        }));

        return { status: 'success', data: response.data };
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to bulk delete tables');
    }
  };
};

// ─── Column Management ──────────────────────────────────────────────────────

export const addColumn = (tableUuid: string, payload: CreateColumnPayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/columns`, payload);

      if (response.status === 201) {
        const columnData = response.data;

        // Refresh the full table to get updated columns / cells
        await dispatch(fetchBatchTableDetail(tableUuid) as any);

        // Auto-process the new column across all existing documents
        if (columnData?.id) {
          dispatch(processColumn(tableUuid, columnData.id) as any);
        }

        return columnData;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to add column');
    }
    return null;
  };
};

export const updateColumn = (tableUuid: string, columnId: number, payload: UpdateColumnPayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(`/tables/${tableUuid}/columns/${columnId}`, payload);

      if (response.status === 200) {
        // IMPORTANT: await the refetch so it completes before this action returns.
        // Without await, a subsequent processColumn() call would race with this
        // fetch - the stale response would overwrite optimistic loading state.
        await dispatch(fetchBatchTableDetail(tableUuid) as any);
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update column');
    }
    return null;
  };
};

export const deleteColumn = (tableUuid: string, columnId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/${tableUuid}/columns/${columnId}`);

      if (response.status === 200) {
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Column deleted'),
          modalType: 'success',
          duration: 2000,
        }));
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete column');
    }
    return false;
  };
};

// ─── Document Upload ─────────────────────────────────────────────────────────

export interface UploadDocumentsResult {
  /** True when the upload POST succeeded (201), regardless of processing. */
  ok: boolean;
  /** Number of rows the server actually created from this upload. */
  uploadedCount: number;
  /** Files the server rejected (unsupported type, too large, etc.). */
  skipped: SkippedFile[];
  /** True only once the new rows have been accepted for AI processing. */
  processingQueued: boolean;
}

// Upload batching (#2230) now lives in `services/uploads/uploadBatching` - the
// same batcher every AI feature uploads through. Tables' behaviour is
// unchanged apart from the bounds, which dropped to 20 files / 20MB to stay
// well under the EU edge's (unmeasured) request-body limit.

/** Build optimistic, in-progress rows from a /documents upload response so the
 *  detail grid shows them immediately with per-cell loaders. */
const mapUploadedToRows = (uploaded: any[]): TableRow[] =>
  (uploaded || []).map((u: any) => ({
    id: u.id,
    /* NO `uuid`. A table row has no uuid - the backend model has no such
       column and its routes take `<int:row_id>`. Inventing one here is what
       made `createDeepDive` post `/rows/undefined/deep-dive`. */
    file_name: u.file_name ?? '',
    file_type: u.file_type ?? null,
    file_size: u.file_size ?? null,
    sort_order: u.sort_order ?? 0,
    status: 'processing',
    processing_error: null,
    processed_at: null,
    token_count: null,
    cells: {},
  }));

/**
 * Stream documents into a table in the background: upload in bounded batches,
 * and start AI processing for each batch the moment it lands - so the user sees
 * rows appear (with loaders) and results fill in progressively instead of
 * waiting for the whole set to upload first.
 *
 * The `expected_total` sent on each batch tells the backend/AI not to mark the
 * table "completed" until every document has arrived; `upload-complete`
 * reconciles that target to whatever actually uploaded (covers skips/errors).
 *
 * Fire-and-forget: callers don't await this - it keeps running after the create
 * wizard closes or the side panel is dismissed.
 */
// Tables whose in-flight streaming upload should abort. Set by stopProcessing()
// so the user can Stop mid-upload; streamUploadAndProcess checks it between batches.
const _cancelledUploadSessions = new Set<string>();

export const streamUploadAndProcess = (
  tableUuid: string,
  files: File[],
  baselineDocCount?: number,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    if (!files.length || !tableUuid) return;
    _cancelledUploadSessions.delete(tableUuid); // fresh session

    const ct = getState().batchTables.currentTable;
    const baseline = baselineDocCount ?? (ct && ct.uuid === tableUuid ? (ct.document_count || 0) : 0);

    /*
      A STABLE KEY FOR THIS RUN, so a retried `/documents` can be recognised
      as the same batch rather than stored twice. The backend is adding
      idempotency on it; sending it costs nothing until then.
    */
    const runId = `${tableUuid}-${Date.now()}`;

    dispatch({ type: BATCH_TABLES_SET_UPLOAD_PROGRESS, payload: { active: true, tableUuid, uploaded: baseline, total: baseline + files.length } });
    // The table is processing the moment documents start streaming in - reflect
    // that immediately rather than waiting for the first AI event. (Backend
    // persists 'processing' on the first process-rows call below; the AI engine
    // then drives live cell/progress events over pubsub.)
    dispatch({ type: BATCH_TABLES_UPDATE_TABLE_STATUS, payload: { uuid: tableUuid, status: 'processing' } });

    let uploadedTotal = 0;
    /*
      THE RUNNING INTENDED TOTAL, not the number we hoped for.

      This used to be `baseline + files.length`, computed once and sent on every
      batch - so a run of 180 that lost 160 kept telling the backend to expect
      180, and the table sat at `processing` waiting for documents that were
      never coming. Every file we learn is lost comes off the target as we learn
      it, and `upload-complete` reconciles whatever is left at the end.
    */
    let knownLost = 0;
    const intendedTotal = () => baseline + files.length - knownLost;

    /*
      PROCESSING FAILURES DO NOT STOP UPLOADING.

      `process-rows` used to be awaited INSIDE the batcher's `send`, which made
      an AI-queueing failure indistinguishable from a transport failure: one 500
      from it ended the run and the remaining 160 files never went up at all.
      The two are unrelated - a batch that uploaded has uploaded, whether or not
      the rows were accepted for processing - so it lives out here, in a chain
      that cannot throw into the batcher.

      Chained rather than fired loose, so batches are still queued in order and
      results stream in per batch; not awaited inside `send`, so the next
      upload starts while this one is being queued.
    */
    const processingErrors: { batch: number; error: any }[] = [];
    let processChain: Promise<void> = Promise.resolve();

    const queueProcessing = (rowIds: number[], batchIndex: number) => {
      processChain = processChain.then(async () => {
        if (_cancelledUploadSessions.has(tableUuid)) return;
        try {
          await apis.post(`/tables/${tableUuid}/process-rows`, {
            row_ids: rowIds,
            expected_total: intendedTotal(),
          });
        } catch (error: any) {
          processingErrors.push({ batch: batchIndex + 1, error });
          logger.error('[tables] process-rows failed for a batch; upload continues', {
            tableUuid,
            batch: batchIndex + 1,
            rows: rowIds.length,
            status: error?.response?.status,
          });
        }
      });
    };

    const result = await runSequentialUpload(files, {
      /*
        The whole point. Nine batches of twenty unrelated documents: one of them
        failing is a reason to be missing twenty rows, not a hundred and sixty.
      */
      onBatchError: 'continue',
      batchKey: (_batch, index) => `${runId}:${index}`,
      onRetry: ({ index, attempt, delayMs, error }) => {
        logger.warn('[tables] batch upload failed, retrying', {
          tableUuid, batch: index + 1, attempt, delayMs, status: error?.response?.status,
        });
      },
      onBatchFailed: ({ index, files: lost, error }) => {
        knownLost += lost.length;
        logger.error('[tables] batch upload failed for good', {
          tableUuid, batch: index + 1, files: lost.length, status: error?.response?.status,
        });
      },
      send: async (batch, ctx) => {
        const fd = new FormData();
        batch.forEach((file) => fd.append('files', file));

        // Always finish the upload - even after Stop - so the document set is
        // complete (correct count) and resumable. Stop only halts *processing*.
        const resp = await apis.post(`/tables/${tableUuid}/documents`, fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(ctx.batchKey ? { 'X-Upload-Batch-Key': ctx.batchKey } : {}),
          },
        });
        if (resp.status !== 201) return resp;

        const rows = mapUploadedToRows(resp.data?.uploaded || []);
        if (!rows.length) return resp;

        // Show the new rows immediately (with loaders).
        dispatch({ type: BATCH_TABLES_APPEND_ROWS, payload: { tableUuid, rows } });
        uploadedTotal += rows.length;
        dispatch({ type: BATCH_TABLES_SET_UPLOAD_PROGRESS, payload: { uploaded: baseline + uploadedTotal } });

        // If the user pressed Stop, keep uploading but DON'T queue these rows
        // for AI - leave them pending so Continue can pick them up later.
        if (_cancelledUploadSessions.has(tableUuid)) return resp;

        queueProcessing(rows.map((r) => r.id), ctx.index);
        return resp;
      },
    });

    // Let the queued processing settle before reconciling, so `upload-complete`
    // is not racing the last `process-rows`.
    await processChain;

    const cancelled = _cancelledUploadSessions.has(tableUuid);
    _cancelledUploadSessions.delete(tableUuid);

    /*
      RECONCILE, ALWAYS. `upload-complete` is what tells the backend the real
      target, so a run that lost files does not leave the table waiting forever
      for them. It was wrapped in a silent `catch` - if it failed, the table
      kept a wrong `expected_total` and stayed at `processing` with no way for
      anyone to find out why. Now it is logged, and a failure refetches the
      detail so the table at least shows the truth it can get from the server.
    */
    let reconciled = true;
    try {
      await apis.post(`/tables/${tableUuid}/upload-complete`);
    } catch (error: any) {
      reconciled = false;
      logger.error('[tables] upload-complete failed; table may hold a stale expected_total', {
        tableUuid, status: error?.response?.status,
      });
    }

    // Re-assert the stop now that the upload is done. The user's Stop click may
    // have raced the very first batch (server still read 'completed', → 409);
    // by now the table is authoritatively 'processing', so this lands cleanly
    // and the table ends up 'stopped'. (/stop is idempotent.)
    if (cancelled) {
      try {
        await apis.post(`/tables/${tableUuid}/stop`);
      } catch (_e) {
        // best effort
      }
    }

    dispatch({ type: BATCH_TABLES_SET_UPLOAD_PROGRESS, payload: { active: false } });

    // If we stopped mid-upload, or could not reconcile, refetch so the doc set
    // and final state are whatever the server actually holds.
    if (cancelled || !reconciled) dispatch(fetchBatchTableDetail(tableUuid) as any);

    /*
      TELL THE USER WHAT HAPPENED.

      The old code computed `failed` - all 160 names - and threw them away in
      favour of "Some documents failed to upload", a `customMessage` that also
      masked the server's own error. For a run of any size the report is a panel
      listing what landed, what was refused and why, and what failed by name,
      with one button to retry just the failures.
    */
    if (!cancelled && (isPartialUpload(result) || processingErrors.length)) {
      const byName = new Map(files.map((f) => [f.name, f]));
      const retryFiles = result.failed
        .map((name) => byName.get(name))
        .filter((f): f is File => !!f);

      const serverError = result.error
        ? readErrorMessage(result.error, '')
        : undefined;

      if (files.length > UPLOAD_MAX_FILES_PER_BATCH || result.failed.length) {
        dispatch(showSidePanel({
          type: 'tableUploadReport',
          showSidePanel: true,
          width: '30rem',
          message: 'Upload report',
          data: {
            tableUuid,
            attempted: result.attempted,
            uploadedCount: result.uploadedCount,
            skipped: result.skipped,
            failed: result.failed,
            retryFiles,
            processingFailedBatches: processingErrors.length,
            serverError,
          },
        }) as any);
      } else {
        // A small run: the sentence is enough, and it carries the server's own
        // words rather than replacing them.
        const summary = describeUploadResult(result);
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'warning',
          message: summary || 'Some documents failed to upload.',
          subMsg: serverError,
          duration: 8000,
        }));
      }

      if (result.error) {
        dispatch({ type: BATCH_TABLES_ERROR, payload: serverError || 'Some documents failed to upload.' });
      }
    }
  };
};

export const uploadDocuments = (tableUuid: string, files: File[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>): Promise<UploadDocumentsResult> => {
    dispatch({ type: BATCH_TABLES_SET_UPLOADING, payload: true });

    const allUploaded: any[] = [];

    const result = await runSequentialUpload(files, {
      send: async (batch) => {
        const formData = new FormData();
        batch.forEach((file) => formData.append('files', file));
        const response = await apis.post(`/tables/${tableUuid}/documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.status === 201) {
          allUploaded.push(...(response.data?.uploaded || []));
        }
        return response;
      },
    });

    const allSkipped = result.skipped;
    const anySucceeded = allUploaded.length > 0;
    const failed = !result.ok;

    // A later batch failed. Earlier batches may already have created rows, so
    // don't discard them - reconcile below and report the partial honestly.
    if (failed) handleApiError(result.error, dispatch, 'Failed to upload documents');

    try {
      if (!anySucceeded) {
        return { ok: false, uploadedCount: 0, skipped: allSkipped, processingQueued: false };
      }

      // Refresh the full table so the newly created rows are visible even if a
      // later batch failed.
      await dispatch(fetchBatchTableDetail(tableUuid) as any);

      const uploadedRowIds = allUploaded
        .map((r: any) => r.id)
        .filter((id: any) => id != null);

      // Auto-process the newly uploaded rows. We MUST await this: if the
      // process-rows call fails the table would otherwise sit at its previous
      // 'completed' status with new 'pending' rows and nothing running - the
      // #2233 "shows done but nothing happens" symptom.
      let processingQueued = false;
      if (uploadedRowIds.length > 0) {
        const procResult = await dispatch(processRows(tableUuid, uploadedRowIds) as any);
        processingQueued = !!procResult;
      }

      // ok=false if any batch failed, so the caller surfaces a partial-upload
      // warning even though some rows landed.
      return { ok: !failed, uploadedCount: uploadedRowIds.length, skipped: allSkipped, processingQueued };
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to upload documents');
      return { ok: false, uploadedCount: allUploaded.length, skipped: allSkipped, processingQueued: false };
    } finally {
      dispatch({ type: BATCH_TABLES_SET_UPLOADING, payload: false });
    }
  };
};

export const deleteDocument = (tableUuid: string, rowId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/${tableUuid}/documents/${rowId}`);

      if (response.status === 200) {
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Document removed'),
          modalType: 'success',
          duration: 2000,
        }));
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to remove document');
    }
    return false;
  };
};

/**
 * Stream a table document blob for side-panel preview.
 */
export const streamTableDocument = async (tableUuid: string, rowId: number): Promise<Blob> => {
  const response = await apis.get(`/tables/${tableUuid}/documents/${rowId}/stream?disposition=inline`, {
    responseType: 'blob',
  });
  return response.data;
};

// ─── Processing ──────────────────────────────────────────────────────────────

export const startProcessing = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    dispatch({ type: BATCH_TABLES_SET_PROCESSING, payload: true });

    // Immediately optimistically update the table status so skeleton loaders appear
    const currentTable = getState().batchTables.currentTable;
    if (currentTable && currentTable.uuid === tableUuid) {
      dispatch({
        type: BATCH_TABLES_UPDATE_TABLE_STATUS,
        payload: { uuid: tableUuid, status: 'queued' },
      });
      // Also set all pending rows to processing so skeleton loaders show on all cells
      currentTable.rows.forEach((row: any) => {
        if (row.status === 'pending') {
          dispatch({
            type: BATCH_TABLES_UPDATE_ROW_STATUS,
            payload: { row_id: row.id, status: 'processing' },
          });
        }
      });
    }

    try {
      const response = await apis.post(`/tables/${tableUuid}/process`);

      if (response.status === 202) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Processing started - AI is analyzing your documents'),
          modalType: 'success',
          duration: 4000,
        }));

        // Safety-net refetch: don't fetch immediately (it would overwrite
        // optimistic row/cell statuses before PubSub events arrive).
        // Instead wait 15s - by then PubSub will have delivered most updates,
        // and this catches any events that were missed.
        setTimeout(() => {
          dispatch(fetchBatchTableDetail(tableUuid) as any);
        }, 15000);

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to start processing');
    } finally {
      dispatch({ type: BATCH_TABLES_SET_PROCESSING, payload: false });
    }

    return null;
  };
};

// ─── Partial Processing ──────────────────────────────────────────────────────

/**
 * Process a single column across all existing documents.
 * Called automatically after adding a new column.
 */
export const processColumn = (tableUuid: string, columnId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    // Optimistic: set table to processing and mark relevant cells as pending
    const currentTable = getState().batchTables.currentTable;
    if (currentTable && currentTable.uuid === tableUuid) {
      dispatch({
        type: BATCH_TABLES_UPDATE_TABLE_STATUS,
        payload: { uuid: tableUuid, status: 'processing' },
      });
      // Reset cells for this column to 'pending' so skeleton loaders appear
      dispatch({
        type: BATCH_TABLES_RESET_COLUMN_CELLS,
        payload: { columnId },
      });
      // Mark all rows as processing so skeleton loaders show
      currentTable.rows.forEach((row: any) => {
        dispatch({
          type: BATCH_TABLES_UPDATE_ROW_STATUS,
          payload: { row_id: row.id, status: 'processing' },
        });
      });
    }

    try {
      const response = await apis.post(`/tables/${tableUuid}/process-column/${columnId}`);

      if (response.status === 202) {
        // dispatch(showFeedbackModal({
        //   showModal: true,
        //   message: 'Processing new column - analyzing your documents',
        //   modalType: 'success',
        //   duration: 3000,
        // }));

        // Safety-net refetch: if PubSub events are missed (e.g. due to race
        // condition on connect/join), refetch the full table after a delay to
        // ensure cells reflect the latest values.
        setTimeout(() => {
          dispatch(fetchBatchTableDetail(tableUuid) as any);
        }, 15000);

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to process column');
      // Revert optimistic update on error
      dispatch(fetchBatchTableDetail(tableUuid) as any);
    }

    return null;
  };
};

/**
 * Process specific rows (documents) across all columns.
 * Called automatically after uploading new documents.
 */
export const processRows = (tableUuid: string, rowIds: number[], expectedTotal?: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    if (!rowIds.length) return null;

    // Optimistic: set table to processing and mark new rows as processing
    const currentTable = getState().batchTables.currentTable;
    if (currentTable && currentTable.uuid === tableUuid) {
      dispatch({
        type: BATCH_TABLES_UPDATE_TABLE_STATUS,
        payload: { uuid: tableUuid, status: 'processing' },
      });
      rowIds.forEach((rowId) => {
        dispatch({
          type: BATCH_TABLES_UPDATE_ROW_STATUS,
          payload: { row_id: rowId, status: 'processing' },
        });
      });
    }

    try {
      const response = await apis.post(`/tables/${tableUuid}/process-rows`, {
        row_ids: rowIds,
        ...(expectedTotal != null ? { expected_total: expectedTotal } : {}),
      });

      if (response.status === 202) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: `Processing ${rowIds.length} new document${rowIds.length > 1 ? 's' : ''} - AI is analyzing`,
          modalType: 'success',
          duration: 3000,
        }));

        // Safety-net refetch in case PubSub events are missed
        setTimeout(() => {
          dispatch(fetchBatchTableDetail(tableUuid) as any);
        }, 15000);

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to process documents');
      // Revert optimistic update on error
      dispatch(fetchBatchTableDetail(tableUuid) as any);
    }

    return null;
  };
};

// ─── Reset stuck table ──────────────────────────────────────────────────────

export const resetTable = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/reset`);

      if (response.status === 200) {
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Table reset - you can now re-process it'),
          modalType: 'success',
          duration: 3000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to reset table');
    }

    return null;
  };
};

// ─── Stop / Continue / Retry ─────────────────────────────────────────────────

/** Stop an in-progress run. The backend marks it 'stopped' and the AI worker
 *  drains remaining queued messages without doing AI work. Completed results
 *  are kept; the run can be resumed with continueProcessing. */
export const stopProcessing = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    // Tell any in-flight streaming upload loop to stop *queuing* new rows for
    // processing (it keeps uploading so the doc set still completes).
    _cancelledUploadSessions.add(tableUuid);
    // Optimistic: reflect 'stopped' immediately.
    dispatch({ type: BATCH_TABLES_UPDATE_TABLE_STATUS, payload: { uuid: tableUuid, status: 'stopped' } });
    try {
      const response = await apis.post(`/tables/${tableUuid}/stop`);
      if (response.status === 200) {
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Processing stopped'),
          modalType: 'success',
          duration: 2500,
        }));
        return response.data;
      }
    } catch (error: any) {
      // A 409 means the server doesn't see the table as running *yet*.
      if (error?.response?.status === 409) {
        const up = getState().batchTables.uploadProgress;
        const midUpload = up.active && up.tableUuid === tableUuid;
        if (midUpload) {
          // Stop raced the first upload batch. Don't alarm the user: the upload
          // loop is cancelled and streamUploadAndProcess re-asserts /stop once
          // the upload settles. Keep the optimistic 'stopped'.
          return null;
        }
        // Not uploading → the table genuinely wasn't running. Reconcile to truth.
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        return null;
      }
      handleApiError(error, dispatch, 'Failed to stop processing');
      dispatch(fetchBatchTableDetail(tableUuid) as any);
    }
    return null;
  };
};

/** Resume a stopped/stalled run, picking up where it left off.
 *
 * The set of rows to (re)process is computed on the BACKEND from authoritative
 * status - NOT from the FE snapshot - so already-completed documents are never
 * re-run (the FE can lag behind in-flight rows that finished after Stop). The
 * server also excludes rows still finishing in-flight on a stopped table. */
export const continueProcessing = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    const ct = getState().batchTables.currentTable;
    const docCount = ct && ct.uuid === tableUuid ? ct.document_count : undefined;
    // Optimistic: reflect 'processing' immediately so the UI flips state.
    dispatch({ type: BATCH_TABLES_UPDATE_TABLE_STATUS, payload: { uuid: tableUuid, status: 'processing' } });
    try {
      // expected_total = document_count: all docs are present, so the finalize
      // gate is satisfied as soon as the remaining rows finish.
      const resp = await apis.post(
        `/tables/${tableUuid}/resume`,
        docCount != null ? { expected_total: docCount } : {},
      );
      if (resp.status === 200) {
        if (resp.data?.rows === 0) {
          // Nothing left - undo the optimistic 'processing' and take the status
          // the server reconciled to (e.g. 'completed' or back to 'stopped').
          dispatch({ type: BATCH_TABLES_UPDATE_TABLE_STATUS, payload: { uuid: tableUuid, status: resp.data.status || 'stopped' } });
          dispatch(showFeedbackModal({ showModal: true, message: translate('Nothing left to process'), modalType: 'info', duration: 2500 }));
        }
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        return resp.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to resume processing');
      dispatch(fetchBatchTableDetail(tableUuid) as any);
    }
    return null;
  };
};

/** Re-run only the rows that failed. */
export const retryFailed = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    const ct = getState().batchTables.currentTable;
    if (!ct || ct.uuid !== tableUuid) return null;
    const ids = ct.rows.filter((r) => r.status === 'failed').map((r) => r.id);
    if (!ids.length) return null;
    return dispatch(processRows(tableUuid, ids, ct.document_count) as any);
  };
};

// ─── Export ──────────────────────────────────────────────────────────────────

export const exportTable = (tableUuid: string, format: 'json' | 'csv' = 'csv') => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get(`/tables/${tableUuid}/export`, {
        params: { format },
        responseType: format === 'csv' ? 'blob' : 'json',
      });

      if (response.status === 200) {
        if (format === 'csv') {
          // Download as file
          const blob = new Blob([response.data], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `table_${tableUuid}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }

        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Export complete'),
          modalType: 'success',
          duration: 2000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to export table');
    }
    return null;
  };
};

// ─── Templates ───────────────────────────────────────────────────────────────

export const fetchTemplates = (category?: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: BATCH_TABLES_LOADING_TEMPLATES, payload: true });

    try {
      const response = await apis.get('/tables/templates', {
        params: category ? { category } : {},
      });

      if (response.status === 200) {
        dispatch({
          type: BATCH_TABLES_SET_TEMPLATES,
          payload: response.data.items || [],
        });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: BATCH_TABLES_LOADING_TEMPLATES, payload: false });
    }

    return null;
  };
};

export const saveTableAsTemplate = (tableUuid: string, payload: SaveAsTemplatePayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/save-template`, payload);

      if (response.status === 201) {
        dispatch(fetchTemplates() as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Template saved'),
          modalType: 'success',
          duration: 3000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to save template');
    }
    return null;
  };
};

export const createTemplate = (payload: CreateTemplatePayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/tables/templates', payload);

      if (response.status === 201) {
        dispatch(fetchTemplates() as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Template created'),
          modalType: 'success',
          duration: 3000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to create template');
    }
    return null;
  };
};

export const getTemplate = (templateUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get(`/tables/templates/${templateUuid}`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to load template');
    }
    return null;
  };
};

export const updateTemplate = (templateUuid: string, payload: Partial<CreateTemplatePayload>) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(`/tables/templates/${templateUuid}`, payload);
      if (response.status === 200) {
        dispatch(fetchTemplates() as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Template updated'),
          modalType: 'success',
          duration: 3000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update template');
    }
    return null;
  };
};

export const deleteTableTemplate = (templateUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/templates/${templateUuid}`);
      if (response.status === 200) {
        dispatch(fetchTemplates() as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Template deleted'),
          modalType: 'success',
          duration: 3000,
        }));
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete template');
    }
    return false;
  };
};

// ─── SSE Table Creation ──────────────────────────────────────────────────────

/**
 * Create a batch table, then return immediately so the user lands on the table
 * detail screen while documents stream in.
 *
 *   1. Create the table (metadata only - instant).
 *   2. Resolve to the caller so the wizard navigates to the new table.
 *   3. Kick off streamUploadAndProcess in the BACKGROUND - documents upload in
 *      batches and start processing as they land, shown live on the detail
 *      screen (rows appear with loaders + an "Uploading X/Y" indicator).
 *
 * Still emits BATCH_TABLE_CREATE_* events for the wizard's "Creating table" step.
 */
export const createBatchTableSSE = (
  tableData: CreateTablePayload,
  files: File[]
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>): Promise<any> => {
    dispatch({ type: BATCH_TABLE_CREATE_RESET });
    dispatch({ type: BATCH_TABLE_CREATE_CONNECTING });

    const steps = [{ id: 1, name: 'Creating table', status: 'pending' }];
    const emitStep = (status: string) => {
      steps[0] = { ...steps[0], status };
      dispatch({ type: BATCH_TABLE_CREATE_STEP, payload: { step: steps[0] } });
    };

    dispatch({ type: BATCH_TABLE_CREATE_CONNECTED, payload: { message: translate('Creating table') } });
    dispatch({ type: BATCH_TABLE_CREATE_STEPS, payload: { steps } });

    try {
      emitStep('in_progress');
      const created = await dispatch(createBatchTable(tableData) as any);
      if (!created || !created.uuid) {
        emitStep('failed');
        dispatch({ type: BATCH_TABLE_CREATE_FAILED, payload: { error: 'Failed to create table' } });
        throw new Error('create_table_failed');
      }
      emitStep('completed');

      // Background upload + per-batch processing. NOT awaited - it continues
      // after the wizard closes and the user is on the table detail screen.
      if (files.length > 0) {
        dispatch(streamUploadAndProcess(created.uuid, files, 0) as any);
        dispatch(showFeedbackModal({ showModal: true, message: `Uploading ${files.length} document${files.length > 1 ? 's' : ''}…`, modalType: 'success', duration: 2500 }));
      } else {
        dispatch(showFeedbackModal({ showModal: true, message: translate('Table created'), modalType: 'success', duration: 2500 }));
      }

      dispatch({ type: BATCH_TABLE_CREATE_COMPLETED, payload: { ok: true, message: translate('Table created'), table: created } });
      return { ok: true, table: created };
    } catch (err) {
      if (err instanceof Error && err.message === 'create_table_failed') {
        throw err; // createBatchTable already surfaced the error
      }
      dispatch({ type: BATCH_TABLE_CREATE_FAILED, payload: { error: String(err) } });
      dispatch(showFeedbackModal({ showModal: true, message: translate('Table creation failed.'), modalType: 'error', duration: 3500 }));
      throw err;
    }
  };
};

export const resetBatchTableCreation = () => (dispatch: any) => {
  dispatch({ type: BATCH_TABLE_CREATE_RESET });
};


// ─── Collaborator Management Actions ────────────────────────────────────────

export const BATCH_TABLES_SET_COLLABORATORS = 'BATCH_TABLES_SET_COLLABORATORS';

/**
 * Fetch collaborators for a table.
 */
export const fetchTableCollaborators = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const res = await apis({
        method: 'GET',
        url: `/tables/${tableUuid}/collaborators`,
      });
      dispatch({ type: BATCH_TABLES_SET_COLLABORATORS, payload: res.data?.collaborators || [] });
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to load collaborators');
    }
  };
};

/**
 * Add a collaborator to a table.
 */
export const addTableCollaborator = (tableUuid: string, collaboratorIdOrUuid: string | number, access: string = 'view') => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const data: any = { access };

      // Determine if it's a numeric ID (legacy) or a UUID string
      if (typeof collaboratorIdOrUuid === 'number' || !isNaN(Number(collaboratorIdOrUuid))) {
        data.user_id = Number(collaboratorIdOrUuid);
      } else {
        // UUID string from org-people API / CollaboratorSelector
        data.collaborator_uuid = collaboratorIdOrUuid;
      }

      await apis({
        method: 'POST',
        url: `/tables/${tableUuid}/collaborators`,
        data,
      });
      dispatch(fetchTableCollaborators(tableUuid) as any);
      dispatch(showFeedbackModal({
        showModal: true,
        message: translate('Access granted'),
        modalType: 'success',
        duration: 2500,
      }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to grant access');
    }
  };
};

/**
 * Update a collaborator's access level.
 */
export const updateTableCollaboratorAccess = (tableUuid: string, collaboratorUuid: string, access: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      await apis({
        method: 'PUT',
        url: `/tables/${tableUuid}/collaborators/${collaboratorUuid}`,
        data: { access },
      });
      dispatch(fetchTableCollaborators(tableUuid) as any);
      // dispatch(showFeedbackModal({
      //   showModal: true,
      //   message: 'Access updated',
      //   modalType: 'success',
      //   duration: 2500,
      // }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to update access');
    }
  };
};

/**
 * Remove a collaborator from a table.
 */
export const removeTableCollaborator = (tableUuid: string, collaboratorUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      await apis({
        method: 'DELETE',
        url: `/tables/${tableUuid}/collaborators/${collaboratorUuid}`,
      });
      dispatch(fetchTableCollaborators(tableUuid) as any);
      dispatch(showFeedbackModal({
        showModal: true,
        message: translate('Access removed'),
        modalType: 'success',
        duration: 2500,
      }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to remove collaborator');
    }
  };
};


// =============================================================================
// Template Collaborator Actions
// =============================================================================

/**
 * Get collaborators for a table template.
 */
export const fetchTemplateCollaborators = (templateUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get(`/tables/templates/${templateUuid}/collaborators`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    }
    return null;
  };
};

/**
 * Add a collaborator to a table template.
 */
export const addTemplateCollaborator = (templateUuid: string, collaboratorIdOrUuid: string | number, access: string = 'view') => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const data: any = { access };
      if (typeof collaboratorIdOrUuid === 'number' || !isNaN(Number(collaboratorIdOrUuid))) {
        data.user_id = Number(collaboratorIdOrUuid);
      } else {
        data.collaborator_uuid = collaboratorIdOrUuid;
      }
      await apis({
        method: 'POST',
        url: `/tables/templates/${templateUuid}/collaborators`,
        data,
      });
      dispatch(showFeedbackModal({
        showModal: true,
        message: translate('Access granted to template'),
        modalType: 'success',
        duration: 2500,
      }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to grant access');
    }
  };
};

/**
 * Update a template collaborator's access level.
 */
export const updateTemplateCollaboratorAccess = (templateUuid: string, collaboratorUuid: string, access: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      await apis({
        method: 'PUT',
        url: `/tables/templates/${templateUuid}/collaborators/${collaboratorUuid}`,
        data: { access },
      });
      // dispatch(showFeedbackModal({
      //   showModal: true,
      //   message: 'Access updated',
      //   modalType: 'success',
      //   duration: 2500,
      // }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to update access');
    }
  };
};

/**
 * Remove a collaborator from a table template.
 */
export const removeTemplateCollaborator = (templateUuid: string, collaboratorUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      await apis({
        method: 'DELETE',
        url: `/tables/templates/${templateUuid}/collaborators/${collaboratorUuid}`,
      });
      dispatch(showFeedbackModal({
        showModal: true,
        message: translate('Access removed from template'),
        modalType: 'success',
        duration: 2500,
      }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to remove access');
    }
  };
};

// ─── Cell Comment Actions ────────────────────────────────────────────────────

/**
 * Fetch all comments for a specific cell.
 */
export const fetchCellComments = (tableUuid: string, cellId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: BATCH_TABLES_LOADING_CELL_COMMENTS, payload: true });
    try {
      const response = await apis.get(`/tables/${tableUuid}/cells/${cellId}/comments`);
      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_SET_CELL_COMMENTS, payload: response.data });
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to load comments');
    } finally {
      dispatch({ type: BATCH_TABLES_LOADING_CELL_COMMENTS, payload: false });
    }
  };
};

/**
 * Add a comment to a cell. Updates comment_count on the cell in Redux state.
 */
export const addCellComment = (tableUuid: string, cellId: number, commentText: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/cells/${cellId}/comments`, {
        comment_text: commentText,
      });
      if (response.status === 201) {
        dispatch({ type: BATCH_TABLES_ADD_CELL_COMMENT, payload: response.data });

        // Update comment_count on the cell in currentTable
        if (response.data.comment_count !== undefined) {
          dispatch({
            type: BATCH_TABLES_UPDATE_CELL,
            payload: {
              row_id: null, // Will be resolved by reducer via cell_id
              column_id: null,
              cell_id: cellId,
              partial: { comment_count: response.data.comment_count },
            },
          });
        }

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to add comment');
    }
    return null;
  };
};

/**
 * Update an existing cell comment. Only the author can edit.
 */
export const updateCellComment = (tableUuid: string, cellId: number, commentId: number, commentText: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(`/tables/${tableUuid}/cells/${cellId}/comments/${commentId}`, {
        comment_text: commentText,
      });
      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_UPDATE_CELL_COMMENT, payload: response.data });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update comment');
    }
    return null;
  };
};

/**
 * Create (or re-attach to) a deep-dive review session for a compliance row.
 *
 * POST /tables/:table_uuid/rows/:row_id/deep-dive - an INT row id.
 *
 * It used to send `row.uuid`. A table row has no uuid: the backend route is
 * `<int:row_id>` and the model has no uuid column, so the only place the field
 * ever existed was `mapUploadedToRows`, which invented `uuid: u.uuid ?? null`
 * for the optimistic rows. Every real row therefore had `undefined` there, and
 * the request went to `/rows/undefined/deep-dive` - a 404 on every attempt, and
 * on rows that never went through the optimistic path the caller's
 * `if (!row.uuid) return` made the button do nothing at all.
 *
 * - 201/200: created → { review_session_uuid }
 * - 409: already exists → pull review_session_uuid from the error body
 */
export const createDeepDive = (tableUuid: string, rowId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/rows/${rowId}/deep-dive`);
      if (response.status === 201 || response.status === 200) {
        const { review_session_uuid } = response.data;
        dispatch({
          type: BATCH_TABLES_UPDATE_ROW_DEEP_DIVE,
          payload: { row_id: rowId, deep_dive_status: 'processing', review_session_uuid },
        });
        return { review_session_uuid, row_id: rowId, alreadyExists: false };
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        const review_session_uuid = error.response.data?.review_session_uuid || null;
        if (review_session_uuid) {
          dispatch({
            type: BATCH_TABLES_UPDATE_ROW_DEEP_DIVE,
            payload: { row_id: rowId, deep_dive_status: 'processing', review_session_uuid },
          });
          return { review_session_uuid, row_id: rowId, alreadyExists: true };
        }
      }
      handleApiError(error, dispatch, 'Failed to start deep dive');
    }
    return null;
  };
};

/**
 * Create one ReviewSession spanning multiple compliance-table rows ("Review for Repaper").
 * Each selected row is linked to the new session and marked deep_dive_status='processing'.
 * Rows that already had a deep dive are returned in `skipped`.
 */
export const bulkCreateDeepDive = (tableUuid: string, rowIds: number[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/bulk-deep-dive`, { row_ids: rowIds });
      if (response.status === 202 || response.status === 200) {
        const { review_uuid, document_count, skipped } = response.data;
        // Mark all included rows as processing in the local table state
        const skippedIds = new Set((skipped || []).map((s: any) => s.row_id));
        rowIds.forEach((rid) => {
          if (!skippedIds.has(rid)) {
            dispatch({
              type: BATCH_TABLES_UPDATE_ROW_DEEP_DIVE,
              payload: { row_id: rid, deep_dive_status: 'processing', review_session_uuid: review_uuid },
            });
          }
        });
        return { review_uuid, document_count, skipped };
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to start bulk review');
    }
    return null;
  };
};

/**
 * Delete a cell comment. Only the author can delete. Updates comment_count.
 */
export const deleteCellComment = (tableUuid: string, cellId: number, commentId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/${tableUuid}/cells/${cellId}/comments/${commentId}`);
      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_REMOVE_CELL_COMMENT, payload: { commentId } });

        // Update comment_count on the cell in currentTable
        if (response.data.comment_count !== undefined) {
          dispatch({
            type: BATCH_TABLES_UPDATE_CELL,
            payload: {
              row_id: null,
              column_id: null,
              cell_id: cellId,
              partial: { comment_count: response.data.comment_count },
            },
          });
        }

        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete comment');
    }
    return false;
  };
};

// ─── Playbook Sync ────────────────────────────────────────────────────────────

/**
 * Sync compliance columns with the linked playbook.
 * Adds columns for new rules, detaches columns for removed rules.
 * Optionally accepts row_ids to reprocess those rows for new columns.
 */
export const syncPlaybook = (tableUuid: string, rowIds?: number[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: BATCH_TABLES_SYNC_PLAYBOOK_START });
    try {
      const response = await apis.post(`/tables/${tableUuid}/sync-playbook`, {
        ...(rowIds && rowIds.length > 0 ? { row_ids: rowIds } : {}),
      });

      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_SYNC_PLAYBOOK_DONE });
        // Refresh the table to get updated columns
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        return response.data;
      }
    } catch (error) {
      dispatch({ type: BATCH_TABLES_SYNC_PLAYBOOK_DONE });
      handleApiError(error, dispatch, 'Failed to sync playbook');
    }
    return null;
  };
};

// ─── Row Jurisdiction ─────────────────────────────────────────────────────────

/**
 * Set or clear a per-row jurisdiction override.
 */
export const setRowJurisdiction = (tableUuid: string, rowId: number, jurisdictionOverride: string | null) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(`/tables/${tableUuid}/rows/${rowId}/jurisdiction`, {
        jurisdiction_override: jurisdictionOverride,
      });

      if (response.status === 200) {
        dispatch({
          type: BATCH_TABLES_UPDATE_ROW_JURISDICTION,
          payload: { rowId, jurisdiction_override: jurisdictionOverride },
        });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to set jurisdiction');
    }
    return null;
  };
};
